import { useEffect, useState } from 'react';
import { useDataStore } from '@/lib/store';
import { api } from '@/lib/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { TradingConfig, TradingPair, ConfigFormData } from '@/types';

export default function StrategiesPage() {
  const { configs, pairs, isLoadingConfigs, fetchConfigs, fetchPairs, updateConfig, removeConfig } = useDataStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TradingConfig | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfigs();
    fetchPairs();
  }, []);

  const handleCreate = () => {
    setEditingConfig(null);
    setError(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: TradingConfig) => {
    setEditingConfig(config);
    setError(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (configId: number) => {
    if (!confirm('Are you sure you want to delete this strategy?')) return;

    try {
      await api.deleteConfig(configId);
      removeConfig(configId);
    } catch (err) {
      console.error('Failed to delete config:', err);
      alert('Failed to delete strategy');
    }
  };

  const handleToggleActive = async (config: TradingConfig) => {
    try {
      await api.updateConfig(config.configId, { isActive: !config.isActive });
      updateConfig(config.configId, { isActive: !config.isActive });
    } catch (err) {
      console.error('Failed to toggle config:', err);
    }
  };

  const handleSubmit = async (data: ConfigFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingConfig) {
        await api.updateConfig(editingConfig.configId, data);
        updateConfig(editingConfig.configId, data as Partial<TradingConfig>);
      } else {
        await api.createConfig(data);
        // Refetch to get full config data
        await fetchConfigs();
      }
      setIsModalOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save strategy';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activePairs = pairs.filter((p) => p.isActive);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Trading Strategies</h1>
          <p className="text-dark-400 text-sm sm:text-base">Configure your automated trading strategies</p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary text-sm sm:text-base">
          + New Strategy
        </button>
      </div>

      {/* Strategies List */}
      {isLoadingConfigs ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : configs.length > 0 ? (
        <div className="grid gap-4">
          {configs.map((config) => (
            <StrategyCard
              key={config.configId}
              config={config}
              onEdit={() => handleEdit(config)}
              onDelete={() => handleDelete(config.configId)}
              onToggle={() => handleToggleActive(config)}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <div className="text-dark-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-4 opacity-50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
            <p className="text-lg mb-2">No strategies configured</p>
            <p className="text-sm">Create your first trading strategy to start automated trading</p>
          </div>
          <button onClick={handleCreate} className="btn btn-primary">
            Create Strategy
          </button>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <StrategyModal
          config={editingConfig}
          pairs={activePairs}
          isSubmitting={isSubmitting}
          error={error}
          onSubmit={handleSubmit}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}

interface StrategyCardProps {
  config: TradingConfig;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}

function StrategyCard({ config, onEdit, onDelete, onToggle }: StrategyCardProps) {
  return (
    <div className="card p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Header row with pair, chain, and status */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-white">
              {config.baseSymbol}/{config.quoteSymbol}
            </h3>
            <span className="px-2 py-0.5 text-xs rounded bg-dark-700 text-dark-300">
              {config.chainName}
            </span>
            <button
              onClick={onToggle}
              className={`px-2 py-0.5 text-xs rounded font-medium ${
                config.isActive
                  ? 'bg-green-900/50 text-green-400'
                  : 'bg-dark-700 text-dark-400'
              }`}
            >
              {config.isActive ? 'Active' : 'Paused'}
            </button>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div>
              <p className="text-xs text-dark-400 mb-0.5">Trigger</p>
              <p className="text-white font-medium text-sm sm:text-base">
                {(config.triggerPercentage * 100).toFixed(2)}%
              </p>
            </div>
            <div>
              <p className="text-xs text-dark-400 mb-0.5">Trade %</p>
              <p className="text-white font-medium text-sm sm:text-base">
                {(config.tradePercentage * 100).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-xs text-dark-400 mb-0.5">Min</p>
              <p className="text-white font-medium text-sm sm:text-base">${config.minimumAmount}</p>
            </div>
            <div>
              <p className="text-xs text-dark-400 mb-0.5">Max</p>
              <p className="text-white font-medium text-sm sm:text-base">${config.maxAmount}</p>
            </div>
            <div>
              <p className="text-xs text-dark-400 mb-0.5">Multiplier</p>
              <p className="text-white font-medium text-sm sm:text-base">{config.multiplier}x</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1 sm:gap-2 sm:ml-4 border-t sm:border-t-0 pt-3 sm:pt-0 mt-2 sm:mt-0">
          <button
            onClick={onEdit}
            className="flex-1 sm:flex-none p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
            title="Edit"
          >
            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="flex-1 sm:flex-none p-2 text-dark-400 hover:text-red-400 hover:bg-dark-700 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

interface StrategyModalProps {
  config: TradingConfig | null;
  pairs: TradingPair[];
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (data: ConfigFormData) => void;
  onClose: () => void;
}

function StrategyModal({ config, pairs, isSubmitting, error, onSubmit, onClose }: StrategyModalProps) {
  const [formData, setFormData] = useState<ConfigFormData>({
    pairId: config?.pairId || (pairs[0]?.pairId || 0),
    tradePercentage: config?.tradePercentage ? config.tradePercentage * 100 : 10,
    triggerPercentage: config?.triggerPercentage ? config.triggerPercentage * 100 : 1,
    minimumAmount: config?.minimumAmount || 10,
    maxAmount: config?.maxAmount || 1000,
    multiplier: config?.multiplier || 1,
    isActive: config?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      tradePercentage: formData.tradePercentage / 100,
      triggerPercentage: formData.triggerPercentage / 100,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50">
      <div className="bg-dark-800 rounded-t-xl sm:rounded-xl border border-dark-700 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-dark-700 sticky top-0 bg-dark-800 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-white">
              {config ? 'Edit Strategy' : 'New Strategy'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="sm:hidden p-2 -mr-2 text-dark-400 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Trading Pair */}
          <div>
            <label className="label">Trading Pair</label>
            <select
              value={formData.pairId}
              onChange={(e) => setFormData({ ...formData, pairId: Number(e.target.value) })}
              className="input"
              disabled={!!config}
            >
              {pairs.map((pair) => (
                <option key={pair.pairId} value={pair.pairId}>
                  {pair.baseSymbol}/{pair.quoteSymbol} ({pair.chainName})
                </option>
              ))}
            </select>
          </div>

          {/* Trigger Percentage */}
          <div>
            <label className="label">
              Trigger Percentage
              <span className="text-dark-400 font-normal ml-2">
                (price change to trigger trade)
              </span>
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                value={formData.triggerPercentage}
                onChange={(e) => setFormData({ ...formData, triggerPercentage: Number(e.target.value) })}
                className="input pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400">%</span>
            </div>
            <p className="text-xs text-dark-400 mt-1">
              Lower = more trades, Higher = fewer trades
            </p>
          </div>

          {/* Trade Percentage */}
          <div>
            <label className="label">
              Trade Percentage
              <span className="text-dark-400 font-normal ml-2">
                (% of balance per trade)
              </span>
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="1"
                max="100"
                value={formData.tradePercentage}
                onChange={(e) => setFormData({ ...formData, tradePercentage: Number(e.target.value) })}
                className="input pr-8"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400">%</span>
            </div>
          </div>

          {/* Min/Max Amount */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Min Amount (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400">$</span>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={formData.minimumAmount}
                  onChange={(e) => setFormData({ ...formData, minimumAmount: Number(e.target.value) })}
                  className="input pl-8"
                />
              </div>
            </div>
            <div>
              <label className="label">Max Amount (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400">$</span>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={formData.maxAmount}
                  onChange={(e) => setFormData({ ...formData, maxAmount: Number(e.target.value) })}
                  className="input pl-8"
                />
              </div>
            </div>
          </div>

          {/* Multiplier */}
          <div>
            <label className="label">
              Multiplier
              <span className="text-dark-400 font-normal ml-2">
                (increase trade size on consecutive trades)
              </span>
            </label>
            <input
              type="number"
              step="0.1"
              min="1"
              max="10"
              value={formData.multiplier}
              onChange={(e) => setFormData({ ...formData, multiplier: Number(e.target.value) })}
              className="input"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between py-2">
            <label className="text-white">Strategy Active</label>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                formData.isActive ? 'bg-primary-600' : 'bg-dark-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  formData.isActive ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn btn-primary flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Strategy'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
